import { DragDropProvider } from '@dnd-kit/react'
import type { DragEndEvent } from '@dnd-kit/react'
import type { FeedbackRTValue, MatchingItem as MatchingItemType } from '@vedalib/editor/lib/elements'
import { TestAnswerFeedbackDisplayOption } from '@vedalib/editor/lib/elements'
import { rtValueToText } from '@vedalib/rich-text'
import * as R from 'ramda'
import { useState } from 'react'
import { useContext } from 'use-context-selector'

import TestFeedback from 'components/Editor/EditorElements/courseKit/TestFeedback'
import { getFeedbackValue } from 'components/Editor/EditorElements/utils/getFeedbackValue'
import { createDndAccessibilityPlugin } from 'utils/accessibility'

import TestMatchingContext from '../TestMatchingContext'
import * as s from './DefaultElementEditor.module.scss'
import DefaultElementEditorItem from './DefaultElementEditorItem'

const SORT_PREFIX = 'sort-'

const getName = (entity: MatchingItemType | null | undefined): string | undefined => {
  if (!entity) {
return undefined
}
  return rtValueToText(entity.leftLabel) || entity.value
}

const DefaultElementEditor: React.FC = () => {
  const {
    styles,
    value,
    isActiveElement,
    onLabelSelect,
    activeIndex,
    isFill,
    font,
    onChange,
    state,
    feedback,
    mode,
  } = useContext(TestMatchingContext)
  const { generalFbValid } = state || {}
  const feedbackValue = getFeedbackValue(value)
  const [isDraggingActive, setIsDraggingActive] = useState(false)

  const handleMove = ({ operation, canceled }: Parameters<DragEndEvent>[0]) => {
    setIsDraggingActive(false)
    if (canceled || !operation.source || !operation.target) {
      return
    }
    const sourceValue = String(operation.source.id)
    const targetValue = String(operation.target.id).replace(SORT_PREFIX, '')
    const sourceIndex = value.items.findIndex((item) => item.value === sourceValue)
    const targetIndex = value.items.findIndex((item) => item.value === targetValue)
    if (sourceIndex === -1 || targetIndex === -1 || sourceIndex === targetIndex) {
      return
    }
    onChange?.(R.assoc('items', R.move(sourceIndex, targetIndex, value.items), value))
  }

  const handleChangeGeneralFeedback = (newFeedbackValue: FeedbackRTValue) => {
    onChange?.(R.assoc('generalFeedback', newFeedbackValue, value))
  }

  return (
    <DragDropProvider
      onDragEnd={handleMove}
      onDragStart={() => setIsDraggingActive(true)}
      plugins={(defaults) => [...defaults, createDndAccessibilityPlugin(getName)]}
    >
      <div
        className={s.matchingElement}
        style={{ marginBottom: `-${styles.indents.marginBottom}` }}
      >
        {value.items.map((item, index) => (
          <DefaultElementEditorItem
            index={index}
            isDraggingInList={isDraggingActive}
            item={item}
            key={item.value}
          />
        ))}
      </div>
      <TestFeedback
        feedbackValue={feedbackValue}
        font={font}
        isTrueAnswer={generalFbValid}
        mode={mode}
        onChange={handleChangeGeneralFeedback}
        rtProps={{
          activeIndex,
          isActiveElement,
          onLabelSelect,
          isFill,
          value: value.generalFeedback,
        }}
        showFeedback={
          value.displayFeedback && value.displayOption !== TestAnswerFeedbackDisplayOption.perOption
        }
        styles={feedback}
      />
    </DragDropProvider>
  )
}

export default DefaultElementEditor
